import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, CircleAlert } from '@/components/icons'
import { Button } from '../ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer'
import { PrntrCard } from '../ui/prntr-input'
import { TextRow } from '../ui/prntr-fields'
import { useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../../lib/orpc'
import { useOnboardingStatus, useUpdateOwnerDetails } from '../../lib/hooks/onboarding'

const ownerDetailsSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  companyName: z.string().min(1, 'Company name is required'),
})

type OwnerDetailsForm = z.infer<typeof ownerDetailsSchema>

interface QuickStartOwnerDetailsDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const PLACEHOLDER_COMPANY_NAME = 'My Company'

export function QuickStartOwnerDetailsDrawer({ isOpen, onClose }: QuickStartOwnerDetailsDrawerProps) {
  const updateOwnerDetails = useUpdateOwnerDetails()
  const { data: status } = useOnboardingStatus()
  const queryClient = useQueryClient()
  const orpc = useOrpcTanstackQueryUtils()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<OwnerDetailsForm>({
    resolver: zodResolver(ownerDetailsSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (!isOpen) return
    const companyName = status?.company?.name ?? ''
    reset({
      firstName: status?.crew?.firstName ?? '',
      lastName: status?.crew?.lastName ?? '',
      companyName: companyName === PLACEHOLDER_COMPANY_NAME ? '' : companyName,
    })
  }, [isOpen, status, reset])

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSave = async (data: OwnerDetailsForm) => {
    await updateOwnerDetails.mutateAsync({
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      companyName: data.companyName.trim(),
    })

    queryClient.invalidateQueries({ queryKey: orpc.onboarding.key() })
    handleClose()
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4 shrink-0">
          <DrawerTitle className="text-lg font-bold text-content-primary-bold tracking-[-1px]">
            Tell Us about You
          </DrawerTitle>
          <button type="button" onClick={handleClose} className="p-1 hover:bg-surface-L2 rounded transition-colors">
            <X className="w-4 h-4 text-content-primary-bold" />
          </button>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="bg-surface-L2 rounded-md flex gap-3 items-center px-3 py-2 mb-2">
            <CircleAlert className="w-5 h-5 text-content-primary-subtle shrink-0" />
            <p className="text-sm text-content-primary-subtle tracking-[0.07px] leading-normal">
              Set your name and company so your crew sees the right details.
            </p>
          </div>

          <div className="flex flex-col gap-3 p-2">
            <PrntrCard title="Your Details">
              <TextRow nullable={false}
                control={control}
                name="firstName"
                label="First Name *"
                placeholder="First name"
                error={errors.firstName?.message}
              />
              <TextRow nullable={false}
                control={control}
                name="lastName"
                label="Last Name *"
                placeholder="Last name"
                error={errors.lastName?.message}
              />
              <TextRow nullable={false}
                control={control}
                name="companyName"
                label="Company Name *"
                placeholder="Your company name"
                error={errors.companyName?.message}
              />
            </PrntrCard>
          </div>
        </div>

        <div className="p-4 shrink-0">
          <Button
            variant="primary"
            size="sm"
            className="w-full"
            disabled={!isValid || isSubmitting}
            onClick={handleSubmit(handleSave)}
          >
            Save & Continue Setup
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
